import { z } from 'zod';
import { addressSchema, blockTagSchema } from './common.js';
/** Use to validate ABI params. */
export const abiParamSchema = z.lazy(() => z.object({
    name: z.string().optional(),
    type: z.string().min(1),
    components: z.array(abiParamSchema).optional(),
    internalType: z.string().optional(),
}).strict());
/** Use to validate ABI entries for reads, sims, and multicalls. */
export const contractAbiFunctionSchema = z.object({
    name: z.string().min(1),
    type: z.enum(['function', 'constructor', 'receive', 'fallback', 'event', 'error']).optional(),
    inputs: z.array(abiParamSchema).optional(),
    outputs: z.array(abiParamSchema).optional(),
    stateMutability: z.enum(['pure', 'view', 'nonpayable', 'payable']).optional(),
}).strict();
/** Use to validate full ABI inputs. */
export const contractAbiSchema = z.array(z.union([contractAbiFunctionSchema, z.string().min(1)]));
/** Use to request one contract read. */
export const readContractParamsSchema = z.object({
    address: addressSchema,
    abi: contractAbiSchema,
    functionName: z.string().min(1),
    args: z.array(z.unknown()).optional(),
    blockTag: blockTagSchema.optional(),
}).strict();
/** Use to request many contract reads in one call. */
export const multicallParamsSchema = z.object({
    calls: z.array(readContractParamsSchema).min(1).max(50),
}).strict();
/** One result in a multicall response. */
export const multicallResultSchema = z.object({
    status: z.enum(['success', 'error']),
    result: z.unknown().optional(),
    error: z.string().min(1).optional(),
}).strict().refine((value) => (value.status === 'success' && value.result !== undefined) || (value.status === 'error' && value.error !== undefined), { message: 'Multicall results must include result for success or error for failure' });
/** Response shape for multicalls. */
export const multicallDataSchema = z.array(multicallResultSchema);
/** Use to simulate a contract call. */
export const simulateContractParamsSchema = readContractParamsSchema.extend({
    from: addressSchema.optional(),
    value: z.string().min(1).optional(),
    gas: z.string().min(1).optional(),
}).strict();
/** Response shape for contract simulations. */
export const simulateContractResultSchema = z.object({
    success: z.boolean(),
    result: z.unknown().optional(),
    error: z.string().min(1).optional(),
    gasUsed: z.union([z.number().int().nonnegative(), z.string().regex(/^\d+$/)]).optional(),
}).strict().refine((value) => (value.success && value.error === undefined) || (!value.success && value.error !== undefined), { message: 'Simulate contract responses must include an error when unsuccessful' });
/** Response shape for contract reads. */
export const contractReadResultSchema = z.unknown();
